// Client-side paging for the Product Library tables, driving the site's
// shared TanStackTablePagination controls (same look as every other table)

// REACT IMPORTS
import { useEffect, useState } from "react";

// CONSTANTS
import { RECORDS_PER_PAGE } from "../../../constants/constants";

/**
 * Pages a list of rows.
 * @param {Array} rows - all rows, already filtered and sorted
 * @param {*} resetKey - back to page 1 when this changes (filters, sort).
 *   Not the rows themselves, so saving a row doesn't jump pages.
 * @returns {{ pageRows, pageStart, table, pagination }} - pass `table` and
 *   `pagination` to TanStackTablePagination
 */
const usePagedRows = (rows, resetKey) => {
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(RECORDS_PER_PAGE);

	useEffect(() => {
		setPageIndex(0);
	}, [resetKey]);

	const pageCount = Math.ceil(rows.length / pageSize);
	// Stay on a real page if rows drop away (e.g. tagged rows leave a filter)
	const index = Math.min(pageIndex, Math.max(pageCount - 1, 0));
	const pageStart = index * pageSize;

	// The parts of a TanStack table the shared pagination controls call
	const table = {
		setPageIndex,
		previousPage: () => setPageIndex(index - 1),
		nextPage: () => setPageIndex(index + 1),
		getCanPreviousPage: () => index > 0,
		getCanNextPage: () => index < pageCount - 1,
		getPageCount: () => pageCount,
		getState: () => ({ pagination: { pageIndex: index, pageSize } }),
		setPageSize: (size) => {
			setPageSize(size);
			setPageIndex(0);
		},
	};

	return {
		pageRows: rows.slice(pageStart, pageStart + pageSize),
		pageStart,
		table,
		pagination: { pageIndex: index, pageSize },
	};
};

export default usePagedRows;
